"use client";

import { useContext, useState } from "react";
import Link from "next/link";
import { UserSubscriptionPlan } from "@/types/index";

import { SubscriptionPlan } from "@/types/index";
import { pricingData, PRO_EXTRA_BENEFITS } from "@/config/subscriptions";
import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { BillingFormButton } from "@/components/forms/billing-form-button";
import { ModalContext } from "@/components/modals/providers";
import { HeaderSection } from "@/components/shared/header-section";
import { Icons } from "@/components/shared/icons";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";

interface PricingCardsProps {
  userId?: string;
  subscriptionPlan?: UserSubscriptionPlan;
  title?: string;
  subtitle?: string;
}

export function PricingCards({ userId, subscriptionPlan, title, subtitle }: PricingCardsProps) {
  const [isYearly, setIsYearly] = useState<boolean>(true);
  const { setShowSignInModal } = useContext(ModalContext);

  const isReturningSubscriber = !!(subscriptionPlan && (subscriptionPlan.isPaid || subscriptionPlan.isCanceled));

  const toggleBilling = (value: string) => {
    if (value === "yearly" && !isYearly) {
      setIsYearly(true);
    } else if (value === "monthly" && isYearly) {
      setIsYearly(false);
    }
  };

  const freePlan = pricingData.find((p) => p.title === "Free")!;
  const gexPlan = pricingData.find((p) => p.title === "QuantWheel GEX")!;
  const proPlan = pricingData.find((p) => p.title === "QuantWheel PRO")!;

  const SandboxCard = () => (
    <div className="relative flex flex-col overflow-hidden rounded-3xl border shadow-md transition-all duration-200 hover:shadow-lg">
      <div className="min-h-[180px] items-start space-y-4 bg-muted/50 p-8">
        <p className="flex font-urban text-sm font-bold uppercase tracking-wider text-muted-foreground">
          Sandbox (Free)
        </p>
        <div className="flex items-end">
          <span className="text-left text-4xl font-bold leading-6">$0</span>
          <span className="-mb-1 ml-2 text-left text-sm font-medium text-muted-foreground">/forever</span>
        </div>
        <p className="text-xs text-muted-foreground">No signup or card required</p>
      </div>
      <div className="flex h-full flex-col justify-between gap-16 p-8">
        <ul className="space-y-3 text-left text-sm font-medium leading-normal">
          {freePlan.benefits.map((feature) => (
            <li className="flex items-start gap-x-3" key={feature}>
              <Icons.check className="size-5 shrink-0 text-primary" />
              <p>{feature}</p>
            </li>
          ))}
        </ul>
        <Link
          href="/dashboard/wheel"
          className={cn(
            buttonVariants({ variant: "outline", rounded: "full", size: "lg" }),
            "w-full shadow-sm hover:shadow",
          )}
        >
          Launch Sandbox
        </Link>
      </div>
    </div>
  );

  const FeatureText = ({ text }: { text: string }) => {
    if (text.includes("Full Wheel Lifecycle")) {
      const [before, after] = text.split("Full Wheel Lifecycle");
      return <>{before}<span className="underline decoration-primary/50 decoration-2 underline-offset-2">Full Wheel Lifecycle</span>{after}</>;
    }
    if (text.includes("GEX PlayTape")) {
      const [before, after] = text.split("GEX PlayTape");
      return <>{before}<span className="underline decoration-zinc-400/60 decoration-2 underline-offset-2">GEX PlayTape</span>{after}</>;
    }
    return <>{text}</>;
  };

  const PaidCard = ({ offer }: { offer: SubscriptionPlan }) => {
    const regularYearlySavings = offer.prices.monthly * 12 - offer.prices.yearly;
    const isPro = offer.title === "QuantWheel PRO";
    const isGex = offer.title === "QuantWheel GEX";

    const benefits = isPro ? PRO_EXTRA_BENEFITS : offer.benefits;

    return (
      <div
        className={cn(
          "relative flex flex-col overflow-hidden rounded-3xl border shadow-md transition-all duration-200 hover:shadow-lg",
          isPro && "-m-0.5 border-2 border-primary",
        )}
        key={offer.title}
      >
        {isPro && (
          <div className="absolute right-0 top-0 px-3 py-1 text-xs font-semibold text-white z-10 bg-primary">
            MOST POPULAR
          </div>
        )}

        <div className="80px] items-start space-y-4 bg-muted/50 p-8">
          <div>
            <p className="font-urban text-sm font-bold uppercase tracking-wider text-muted-foreground">
              QuantWheel{" "}
              <span className={cn(
                isPro && "text-primary drop-shadow-[0_0_6px_hsl(var(--primary)/0.4)]",
                isGex && "text-zinc-500 dark:text-zinc-300",
              )}>
                {isPro ? "PRO" : "GEX"}
              </span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {isGex ? "For directional traders" : "For Wheel and Option Sellers"}
            </p>
          </div>

          <div className="flex flex-row">
            <div className="flex items-end">
              <div className="flex text-left text-4xl font-bold leading-6">
                {isYearly ? (
                  <>
                    <span className="mr-2 text-muted-foreground/80 line-through text-2xl">
                      ${Math.floor(offer.prices.monthly)}
                    </span>
                    <span className={isPro ? "text-primary" : ""}>${Math.floor(offer.prices.yearly / 12)}</span>
                  </>
                ) : (
                  <span className={isPro ? "text-primary" : ""}>
                    ${offer.prices.monthly}
                  </span>
                )}
              </div>
              <div className="-mb-1 ml-2 text-left text-sm font-medium text-muted-foreground">
                <div>/month</div>
              </div>
            </div>
          </div>

          <div className="text-left text-sm text-muted-foreground">
            {!isReturningSubscriber && (
              <div className="mb-1">
                {isGex ? (
                  <>
                    <span className="font-semibold text-amber-600 dark:text-amber-400">$1 for 7 days</span>
                    <span className="text-muted-foreground"> then:</span>
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-amber-600 dark:text-amber-400">$1 for 7 days</span>
                    <span className="text-muted-foreground"> then:</span>
                  </>
                )}
              </div>
            )}
            {isYearly
              ? `$${offer.prices.yearly}/year billed annually (save $${regularYearlySavings})`
              : `$${offer.prices.monthly}/month`}
          </div>
        </div>

        <div className="flex h-full flex-col justify-between gap-16 p-8">
          <ul className="space-y-3 text-left text-sm font-medium leading-normal">
            {benefits.map((feature) => (
              <li className="flex items-start gap-x-3" key={feature}>
                <Icons.check className="size-5 shrink-0 text-primary" />
                <p><FeatureText text={feature} /></p>
              </li>
            ))}

            {isPro && (
              <li className="flex items-start gap-x-3 pt-2 border-t border-border/50">
                <Icons.check className="size-5 shrink-0 text-zinc-400 dark:text-zinc-500" />
                <p className="text-muted-foreground">Everything in QuantWheel GEX</p>
              </li>
            )}
          </ul>

          {userId ? (
            isReturningSubscriber ? (
              <BillingFormButton
                year={isYearly}
                offer={offer}
                subscriptionPlan={subscriptionPlan!}
                shouldStartTrial={false}
              />
            ) : (
              <BillingFormButton
                year={isYearly}
                offer={offer}
                subscriptionPlan={subscriptionPlan || {
                  title: "Free",
                  description: "Free plan",
                  benefits: [],
                  limitations: [],
                  prices: { monthly: 0, yearly: 0 },
                  stripeIds: { monthly: null, yearly: null },
                  isPaid: false,
                  isCanceled: false,
                  stripeCurrentPeriodEnd: 0,
                  stripeCustomerId: null,
                  stripePriceId: null,
                  stripeSubscriptionId: null,
                  interval: null,
                } as UserSubscriptionPlan}
                shouldStartTrial={true}
              />
            )
          ) : (
            <Button
              variant={isPro ? "default" : "outline"}
              size="lg"
              rounded="full"
              className="shadow-sm hover:shadow"
              onClick={() => {
                if (typeof window !== "undefined") {
                  const billingCycle = isYearly ? "yearly" : "monthly";
                  sessionStorage.setItem("trialIntent", `${offer.title.toLowerCase().replace("quantwheel ", "")}|${billingCycle}`);
                }
                setShowSignInModal(true);
              }}
            >
              {isGex ? "Start 7 Days for $1" : "Start 7 Days for $1"}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <MaxWidthWrapper>
      <section id="pricing" className="flex flex-col items-center text-center py-32">
        <HeaderSection
          label="Pricing"
          title={title ?? "Choose the plan that's right for you"}
          subtitle={subtitle ?? "Try our sandbox for free, or start a trial. Cancel anytime."}
        />

        <div className="mb-6 mt-10 flex items-center gap-5">
          <ToggleGroup
            type="single"
            size="sm"
            value={isYearly ? "yearly" : "monthly"}
            onValueChange={toggleBilling}
            aria-label="toggle-year"
            className="h-auto sm:h-9 flex-col sm:flex-row overflow-hidden rounded-2xl sm:rounded-full border bg-background p-1 *:text-muted-foreground gap-1 sm:gap-0"
          >
            <ToggleGroupItem
              value="yearly"
              className="rounded-xl sm:rounded-full px-4 sm:px-5 py-2 sm:py-1 text-xs sm:text-sm data-[state=on]:!bg-primary data-[state=on]:!text-primary-foreground whitespace-nowrap"
              aria-label="Toggle yearly billing"
            >
              Yearly (save up to 30%)
            </ToggleGroupItem>
            <ToggleGroupItem
              value="monthly"
              className="rounded-xl sm:rounded-full px-4 sm:px-5 py-2 sm:py-1 text-xs sm:text-sm data-[state=on]:!bg-primary data-[state=on]:!text-primary-foreground whitespace-nowrap"
              aria-label="Toggle monthly billing"
            >
              Monthly
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="grid gap-5 bg-inherit py-5 lg:grid-cols-3 max-w-6xl mx-auto">
          <SandboxCard />
          {[gexPlan, proPlan].map((offer) => (
            <PaidCard offer={offer} key={offer.title} />
          ))}
        </div>

        <Link
          href="/dashboard/wheel"
          className={cn(
            buttonVariants({ variant: "outline", size: "sm" }),
            "mt-6 text-xs text-muted-foreground",
          )}
        >
          Or try everything in Sandbox first
        </Link>

        <p className="mt-6 text-balance text-center text-base text-muted-foreground">
          Email{" "}
          <a
            className="font-medium text-primary hover:underline"
            href="mailto:hello@quantwheel.com"
          >
            hello@quantwheel.com
          </a>{" "}
          to contact our support team.
          <br />
          <br></br>
        </p>
      </section>
      <br></br>
    </MaxWidthWrapper>
  );
}
